import moment, { Moment } from "moment";
import { ExpandedRatingOptions, RatingAssumption, SaleMethod, SecurityType, TaxDesignation, USState } from "./utils/primary-constants.ts";
import { Bond, Redemption, SerialBond, TermBond } from "../pages/debt-service-page.tsx";
import { BuilderOutput, ProductionTable, ProductionUtils } from "./utils/production.utils.ts";
import Result from "../utils/result.utils.ts";
import { PrimaryTimeUtils } from "./utils/primary-time.utils.ts";
import { Category, SourcesAndUsesUtils } from "./utils/sources-and-uses.utils.ts";
import { lerp } from "../../tools/solvers.ts";
import { DebtServiceUtils, DebtServiceTable } from "./utils/debt-service.utils.ts";
import { v4 as uuidv4 } from "uuid";

export enum DealStatus {
  DRAFT = "Draft",
  PENDING = "Pending",
  WON = "Won",
  CLOSED = "Closed",
  LOST = "Lost",
}

interface DealBuilderProps {
  // Issuance title information
  borrower: string;
  issuanceTitle: string;

  // Required to calculate debt service
  firstCouponDate: Moment;
  deliveryDate: Moment;
  totalTargetParAmount: number;
  denomination: number;
  capitalizedInterest: number | Moment;
  bonds: Bond[];

  // Required to obtain yield spread predictions 
  marketConditionsDate: Moment;
  stateOfIssuance: USState;
  securityType: SecurityType;
  ratingAssumption: RatingAssumption;
  taxDesignation: TaxDesignation;
  saleMethod: SaleMethod;
  callDate: Moment | undefined;
  stateEnhancement: ExpandedRatingOptions | undefined;
  bondInsurance: boolean;
  cushion: number;

  // Sources and uses data
  sourcesAndUses: Category[];

  // Backend stuff
  fetchCouponsAndSpreads: (builder: DealBuilder, bonds: Bond[], predictCoupons: boolean) => Promise<Result<BuilderOutput>>;
  getAlignedRealTimeAAA: (marketConditionsDate: Moment, principalDates: string[]) => Promise<[number[], any]>;
  getAlignedAAA: (marketConditionsDate: Moment, principalDates: string[]) => Promise<[number[], any]>;
  getAlignedUSTreasury: (marketConditionsDate: Moment, principalDates: string[]) => Promise<[number[], any]>;
  saveDeal: (builder: DealBuilder) => Promise<Result<undefined>>;

  // Update principals
  updatePrincipals?: boolean
}

export interface DealBuilderBond {
  maturity: Moment,
  parAmount: number,
  couponRate: number,
  interest: number,
  debtService: number,
  annualDebtService: number | undefined,
  isPrincipal: boolean,
}

const DEFAULT_COUPON = 0.05;
const DEFAULT_DEAL_STATUS = DealStatus.DRAFT;
const DEFAULT_SAVED_DATE = moment().startOf("day");
const DEFAULT_BID_RATE = 0.04;
const DEFAULT_BID_DATE = (delivery: Moment) => delivery.clone().subtract(1, "month");
const DEFAULT_BIDS: [string, number][] = [];
const DEFAULT_COMMENT = "";
const DEFAULT_ADVISOR = "";

export class DealBuilder {

  // Issuance title information
  public borrower: string;
  public issuanceTitle: string;

  // Required to calculate debt service
  public firstCouponDate: Moment;
  public deliveryDate: Moment;
  public totalTargetParAmount: number;
  public denomination: number;
  public capitalizedInterest: number | Moment; // Document
  public bonds: Bond[];

  // Required to obtain yield spread predictions 
  public marketConditionsDate: Moment;
  public stateOfIssuance: USState;
  public securityType: SecurityType;
  public ratingAssumption: RatingAssumption;
  public taxDesignation: TaxDesignation;
  public saleMethod: SaleMethod;
  public callDate: Moment | undefined;
  public stateEnhancement: ExpandedRatingOptions | undefined;
  public bondInsurance: boolean;
  public cushion: number;

  // Sources and uses data
  public sourcesAndUses: Category[];

  // Debt service and production information
  public debtService: DebtServiceTable = [];
  public production: ProductionTable = [];

  // Update backend stuff
  private fetchCouponsAndSpreads: (builder: DealBuilder, bonds: Bond[], predictCoupons: boolean) => Promise<Result<BuilderOutput>>;
  private getAlignedRealTimeAAA: (marketConditionsDate: Moment, principalDates: string[]) => Promise<[number[], any]>;
  private getAlignedAAA: (marketConditionsDate: Moment, principalDates: string[]) => Promise<[number[], any]>;
  private getAlignedUSTreasury: (marketConditionsDate: Moment, principalDates: string[]) => Promise<[number[], any]>;
  private saveDeal: (builder: DealBuilder) => Promise<Result<undefined>>;

  // Database deal id
  public dealId: string;
  public dealStatus: DealStatus;
  public savedDate: Moment;
  public bidDate: Moment;
  public bidRate: number;
  public otherBids: [string, number][];
  public comment: string;
  public advisor: string;

  public isSaved: boolean = false;

  // Caching for performance
  public ticExpenses: number | undefined = undefined;
  public tic: number | undefined = undefined;
  public adjustedTic: number | undefined = undefined;
  public nic: number | undefined = undefined;

  constructor(props: DealBuilderProps) {
    this.borrower = props.borrower;
    this.issuanceTitle = props.issuanceTitle;

    this.firstCouponDate = props.firstCouponDate;
    this.deliveryDate = props.deliveryDate;
    this.totalTargetParAmount = props.totalTargetParAmount;
    this.denomination = props.denomination;
    this.capitalizedInterest = props.capitalizedInterest;
    this.bonds = props.bonds;

    this.marketConditionsDate = props.marketConditionsDate;
    this.stateOfIssuance = props.stateOfIssuance;
    this.securityType = props.securityType;
    this.ratingAssumption = props.ratingAssumption;
    this.taxDesignation = props.taxDesignation;
    this.saleMethod = props.saleMethod;
    this.callDate = props.callDate;
    this.stateEnhancement = props.stateEnhancement;
    this.bondInsurance = props.bondInsurance;
    this.cushion = props.cushion;

    this.sourcesAndUses = props.sourcesAndUses;

    this.fetchCouponsAndSpreads = props.fetchCouponsAndSpreads;
    this.getAlignedAAA = props.getAlignedAAA;
    this.getAlignedRealTimeAAA = props.getAlignedRealTimeAAA;
    this.getAlignedUSTreasury = props.getAlignedUSTreasury;
    this.saveDeal = props.saveDeal;

    this.dealId = uuidv4();
    this.dealStatus = DEFAULT_DEAL_STATUS;
    this.savedDate = DEFAULT_SAVED_DATE.clone();
    this.bidRate = DEFAULT_BID_RATE;
    this.bidDate = DEFAULT_BID_DATE(props.deliveryDate);
    this.otherBids = [...DEFAULT_BIDS];
    this.comment = DEFAULT_COMMENT;
    this.advisor = DEFAULT_ADVISOR;

    this.updateDebtService(props.updatePrincipals === true);
  }

  public deleteBond(bond: Bond) {
    this.bonds = this.bonds.filter(x => x !== bond);
  }

  /**
   * Ungroups a term group into multiple bonds with the same characteristics
   * as the redemption dates of the term bond.
   */
  public ungroupTermBond(bond: TermBond) {
    const newBonds: SerialBond[] = bond.redemptions.map(red => ({
      type: "serial",
      coupon: bond.coupon,
      maturity: red.date,
      principal: red.amount,
      lockedCoupon: bond.lockedCoupon,
      lockedPrincipal: red.locked,
    }))

    this.bonds = this.bonds.filter(x => x !== bond);
    for (const bond of newBonds) {
      this.bonds.push(bond);
    }

    this.bonds.sort((a, b) => moment(a.maturity).diff(moment(b.maturity)));
  }

  /**
   * Groups multiple serial bonds into a single term bond.
   */
  public groupSerialBonds(maturity1: string, maturity2: string) {
    const firstMat = moment.min(moment(maturity1), moment(maturity2));
    const secondtMat = moment.max(moment(maturity1), moment(maturity2));

    const groupBonds: SerialBond[] = this.bonds
      .filter(x => x.type === "serial")
      .filter(x => moment(x.maturity).isBetween(firstMat, secondtMat, "day", "[]"));

    if (groupBonds.length === 0) return;

    const { coupon, maturity } = groupBonds[groupBonds.length - 1];
    const redemptions: Redemption[] = groupBonds.map(x => ({
      amount: x.principal,
      date: x.maturity,
      redemptionPrice: 100,
      locked: x.lockedPrincipal,
    }));

    const lockedCoupon = groupBonds[groupBonds.length - 1].lockedCoupon;
    const termBond: TermBond = { type: "term", coupon, maturity, redemptions, lockedCoupon };
    this.bonds = this.bonds.filter(item => !groupBonds.includes(item as any));
    this.bonds.push(termBond);
    this.bonds.sort((a, b) => moment(a.maturity).diff(moment(b.maturity)));
  }

  public addSerialBond(): SerialBond {
    const date = this.bonds.length === 0 ?
      this.firstCouponDate.clone().add(12, "months") : moment(this.bonds[0].maturity);

    while (this.bonds.some(x => moment(x.maturity).isSame(date, "day"))) {
      date.add(12, "months");
    }

    const bond: SerialBond = {
      type: "serial",
      coupon: DEFAULT_COUPON,
      maturity: date.toISOString(),
      principal: 0,
      lockedCoupon: false,
      lockedPrincipal: false
    };

    this.bonds.push(bond);
    this.bonds.sort((a, b) => moment(a.maturity).diff(moment(b.maturity)));
    return bond;
  }

  public updateBondDate(bond: Bond, date: Moment) {
    bond.maturity = date.toISOString();
    this.bonds.sort((a, b) => moment(a.maturity).diff(moment(b.maturity)));
  }

  public updateDebtService(recalculatePrincipals: boolean = false) {
    if (this.bonds.length === 0) {
      this.debtService = [];
      this.updateProduction();
      this.calculateStats();
      return;
    }

    this.debtService = DebtServiceUtils.calculateDebtService(
      recalculatePrincipals,
      this.deliveryDate,
      this.firstCouponDate,
      this.totalTargetParAmount,
      this.denomination,
      this.bonds,
      this.capitalizedInterest,
    );

    this.calculateStats();
  }

  /**
   * Updates the production calculations. If bonds are provided,
   * they yield will be fetched from the backend.
   */
  public async updateProduction(bonds?: Bond[]): Promise<Result<BuilderOutput>> {
    const output = await this.fetchCouponsAndSpreads(this, bonds || [], false);
    const table = output.valid ? output.body : [];

    this.production = ProductionUtils.calculateProduction(
      this.bonds,
      this.production,
      table.map(x => ({ ...x, spread: x.spread + this.cushion })),
      this.deliveryDate.clone(),
      moment(this.callDate || moment("3000-01-01")),
    );

    this.calculateStats();

    return output;
  }

  public getTotalPrincipal(): number {
    return this.bonds.reduce((prev, curr) => (
      prev + ProductionUtils.getBondPrincipal(curr)
    ), 0)
  }

  public getTotalProduction(): number | undefined {
    let result = 0;

    let temp: number[] = [];

    for (const bond of this.bonds) {
      const production = this.production.find(x => x.bond === bond);
      if (!production) return undefined;
      result += production.production;
      temp.push(production.production)
    }

    return result;
  }

  public getTotalInterest(): number {
    return this.debtService.reduce((prev, curr) => (
      prev + curr.accInterest
    ), 0)
  }

  public getTotalCapitalizedInterest(): number {
    return this.debtService.reduce((prev, curr) => (
      prev + curr.capitalizedInterest
    ), 0)
  }

  public getTotalDebtService(): number {
    return this.debtService.reduce((prev, curr) => (
      prev + curr.debtService
    ), 0)
  }

  public getTotalAnnualDebtService(): number {
    return this.debtService.reduce((prev, curr) => (
      prev + curr.debtService
    ), 0)
  }

  /**
   * Returns the average life of the  bonds in the series.
   *
   * The average life is the length of time the principal of a debt issue is
   * expected to be outstanding. It doesn't take into account interest payments,
   * only principal payments.
   */
  public getAverageLife() {
    const totalPrincipal = this.getTotalPrincipal();
    let result = 0;
    for (const bond of this.bonds) {
      if (bond.type === "serial") {
        const days = PrimaryTimeUtils.daysDifference360(this.deliveryDate, moment(bond.maturity));
        result += (bond.principal * days) / (totalPrincipal * 360);
      } else {
        for (const redemption of bond.redemptions) {
          const days = PrimaryTimeUtils.daysDifference360(this.deliveryDate, moment(redemption.date));
          result += (redemption.amount * days) / (totalPrincipal * 360);
        }
      }
    }

    return result;
  }

  /**
   * Returns the weighted average production maturity of the  bonds in the series.
   *
   * The weighted average production maturity is the length of time the production 
   * value of a debt issue is expected to be outstanding. It doesn't take into account 
   * interest payments, only production amounts.
   */
  public getWeightedAverageMaturity(): number | undefined {
    const totalProduction = this.getTotalProduction();
    if (!totalProduction) return undefined;

    let result = 0;
    for (const bond of this.bonds) {
      const production = this.production.find(x => x.bond === bond);
      if (!production) return undefined;
      if (bond.type === "serial") {
        const days = PrimaryTimeUtils.daysDifference360(this.deliveryDate, moment(bond.maturity));
        result += (bond.principal * (production.price / 100) * days) / (totalProduction * 360);
      } else {
        for (const redemption of bond.redemptions) {
          const days = PrimaryTimeUtils.daysDifference360(this.deliveryDate, moment(redemption.date));
          result += (redemption.amount * (production.price / 100) * days) / (totalProduction * 360);
        }
      }
    }

    return result;
  }

  public getTotalBondYears() {
    let result = 0;
    for (const bond of this.bonds) {
      if (bond.type === "serial") {
        const days = PrimaryTimeUtils.daysDifference360(this.deliveryDate, moment(bond.maturity));
        result += (bond.principal * days) / 360;
      } else {
        for (const redemption of bond.redemptions) {
          const days = PrimaryTimeUtils.daysDifference360(this.deliveryDate, moment(redemption.date));
          result += (redemption.amount * days) / 360;
        }
      }
    }

    return result;
  }

  public getAverageCoupon() {
    if (this.bonds.length === 0) return 0;

    let sum = 0;
    let count = 0;
    for (const bond of this.bonds) {
      if (bond.type === "serial") {
        sum += bond.coupon;
        count += 1;
      } else {
        sum += bond.coupon * bond.redemptions.length;
        count += bond.redemptions.length;
      }
    }

    return sum / count;
  }

  /**
   * Calculates the NIC (total cost of issuance without accounting for the
   * time value of money).
   */
  public calculateNIC(): number | undefined {
    const production = this.getTotalProduction();
    if (!production) return undefined;

    return (
      (
        this.getTotalPrincipal() +
        this.getTotalInterest() -
        production
      ) / this.getTotalBondYears()
    );
  }

  /**
   * Calculates the total amount of money of categories that are relevant
   * to the TIC.
   */
  public calculateTicExpenses(): number | undefined {
    const parAmount = this.getTotalPrincipal();
    const production = this.getTotalProduction();
    if (!production) return undefined;

    const debtService = this.getTotalDebtService();
    return SourcesAndUsesUtils.calculateTicAdjustedExpenses(
      this.sourcesAndUses,
      parAmount,
      debtService
    );
  }

  /**
   * Calculates the TIC (Real total cost of issuance accounting for the time
   * value of money.)
   *
   * @param expenses Cost of issuance in dollars. Use [`0`] if you don't want
   * to ajust the tic to the expenses.
   */
  public calculateTIC(expenses: number = 0): number | undefined {
    const production = this.getTotalProduction();
    if (!production) return undefined;

    const debtResults = this.debtService;
    const deliveryDate = this.deliveryDate;

    function presentValueOfDebtService(tic: number) {
      let result: number = 0;
      const ticPercent = tic / 2 + 1;

      for (const row of debtResults) {
        const date = moment(row.date);
        const days = PrimaryTimeUtils.daysDifference360(deliveryDate, date);
        const rowPv = 1 / Math.pow(ticPercent, days / 180);
        result += row.debtService * rowPv;
      }

      return result;
    }

    // Try and error until we find the result
    return lerp((x) => presentValueOfDebtService(x), production - expenses, 0.05);
  }

  public calculateStats() {
    this.ticExpenses = this.calculateTicExpenses();
    this.tic = this.calculateTIC();
    this.adjustedTic = this.calculateTIC(this.ticExpenses);
    this.nic = this.calculateNIC();
  }


  public updateFirstAndLastPrincipalDate(firstProp: Moment, lastProp: Moment, frequency: number) {
    const first = firstProp.startOf("day").clone();
    const last = lastProp.startOf("day").clone();
    const newBonds: Bond[] = [];

    function addBond(maturity: string) {
      newBonds.push({
        type: "serial",
        coupon: 0.05,
        lockedCoupon: false,
        lockedPrincipal: false,
        maturity,
        principal: 0
      });
    }

    // Keep bonds inside the range
    for (const bond of this.bonds) {
      if (bond.type === "serial") {
        const maturity = moment(bond.maturity);
        if (maturity.isBefore(first, "day") || maturity.isAfter(last, "day")) continue;
        newBonds.push(bond);
      } else if (bond.type === "term") {
        bond.redemptions = bond.redemptions.filter(red => {
          const maturity = moment(red.date);
          return maturity.isSameOrAfter(first, "day") && maturity.isSameOrBefore(last, "day");
        })

        if (bond.redemptions.length > 0) {
          bond.maturity = bond.redemptions[bond.redemptions.length - 1].date;
          newBonds.push(bond);
        }
      }
    }

    // No bonds left, add all the new ones.
    if (newBonds.length === 0) {
      while (first.isSameOrBefore(last, "day")) {
        addBond(first.toISOString());
        first.add(12 / frequency, "months");
      }
    }

    // Extend user bonds
    else {
      const firstMaturity = this.getFirstPrincipalDate(first, newBonds);
      const lastMaturity = this.getLastPrincipalDate(last, newBonds);

      while (first.isBefore(firstMaturity, "day")) {
        addBond(first.toISOString());
        first.add(12 / frequency, "months");
      }

      while (last.isAfter(lastMaturity, "day")) {
        addBond(last.toISOString());
        last.subtract(12 / frequency, "months");
      }
    }

    newBonds.sort((a, b) => moment(a.maturity).diff(moment(b.maturity)));
    this.bonds = newBonds;
  }

  /**
   * Returns the first bond principal date, if there isn't principals, it will
   * return the default value.
   */
  public getFirstPrincipalDate(defaultValue: Moment, bonds?: Bond[]): Moment {
    const bondList = bonds || this.bonds;
    if (bondList.length === 0) return defaultValue.clone();
    return bondList[0].type === "serial" ? moment(bondList[0].maturity) : moment(bondList[0].redemptions[0].date);
  }

  /**
   * Returns the final bond principal date, if there isn't principals, it will
   * return the default value.
   */
  public getLastPrincipalDate(defaultValue: Moment, bonds?: Bond[]): Moment {
    const bondList = bonds || this.bonds;
    if (bondList.length === 0) return defaultValue.clone();
    return moment(bondList[bondList.length - 1].maturity);
  }

  /**
   * Removes and adds bonds to match a principal frequency rate.
   */
  public setPrincipalFrequency(frequency: number, deleteOutOfSchedule: boolean, addMissingBonds: boolean) {
    if (this.bonds.length === 0) return;

    const existingMaturities: Set<string> = new Set();
    const firstPrincipal = this.getFirstPrincipalDate(moment());
    const lastPrincipal = this.getLastPrincipalDate(moment());

    function isInSchedule(date: Moment): boolean {
      // Must be the same day off the month
      const newDay = date.date();
      const firstPrincipalDay = firstPrincipal.date();
      if (newDay !== firstPrincipalDay) return false;

      // Same month and day of the month
      const newMonth = date.month();
      const firstPrincipalMonth = firstPrincipal.month();
      if (newMonth === firstPrincipalMonth) return true;

      // Semi-annual principal frequency
      const diffMonths = Math.abs(newMonth - firstPrincipalMonth);
      return frequency === 2 && diffMonths === 6;
    }

    // Keep only the bonds/repayments in schedule if desired.
    const newBonds: Bond[] = [];
    for (const bond of this.bonds) {

      if (bond.type === "serial") {
        const maturity = moment(bond.maturity);
        existingMaturities.add(maturity.format("YYYY-MM-DD"));
        if (!deleteOutOfSchedule || isInSchedule(maturity)) {
          newBonds.push(bond);
        }
      }

      else {
        bond.redemptions = bond.redemptions.filter(redemption => {
          const maturity = moment(redemption.date);
          existingMaturities.add(maturity.format("YYYY-MM-DD"));
          return !deleteOutOfSchedule || isInSchedule(maturity);
        })

        const redemptionCount = bond.redemptions.length;
        if (redemptionCount !== 0) {
          bond.maturity = bond.redemptions[redemptionCount - 1].date;
        }

        if (redemptionCount >= 2) {
          newBonds.push(bond);
        } else if (redemptionCount === 1) {
          newBonds.push({
            type: "serial",
            coupon: bond.coupon,
            lockedCoupon: bond.lockedCoupon,
            lockedPrincipal: bond.redemptions[0].locked,
            maturity: bond.maturity,
            principal: bond.redemptions[0].amount,
          })
        }
      }
    }

    // Add missing bonds
    if (addMissingBonds) {
      const current = firstPrincipal.clone();
      while (current.isSameOrBefore(lastPrincipal)) {
        if (!existingMaturities.has(current.format("YYYY-MM-DD"))) {
          newBonds.push({
            type: "serial",
            coupon: 0.05,
            lockedCoupon: false,
            lockedPrincipal: false,
            maturity: current.toISOString(),
            principal: 0,
          })
        }
        current.add(12 / frequency, "months");
      }
    }

    this.bonds = newBonds;
    this.bonds.sort((a, b) => moment(a.maturity).diff(moment(b.maturity)));
  }

  public updateMaturitiesAlignment(offsetMonths: number, day: number) {
    for (const bond of this.bonds) {
      bond.maturity = moment(bond.maturity).add(offsetMonths, "months").date(day).toISOString();
      if (bond.type === "term") {
        for (const redemption of bond.redemptions) {
          redemption.date = moment(redemption.date).add(offsetMonths, "months").date(day).toISOString();
        }
      }
    }
  }

  /**
   * Returns the total amount of money in the sources
   */
  public getTotalSourcesAmount(): number {
    const parAmount = this.getTotalPrincipal();
    const production = this.getTotalProduction();
    const debtService = this.getTotalDebtService();
    return SourcesAndUsesUtils.calculateTotalSourceMoney(
      this.sourcesAndUses,
      parAmount,
      production || 0,
      debtService
    );
  }

  /**
   * Returns the amount of money available after paying all the expenses.
   */
  public calculateProjectFunds(): number {
    const sourceMoney = this.getTotalSourcesAmount();
    const issuanceCost = this.getTotalIssuanceCost();
    return sourceMoney - issuanceCost;
  }

  /**
   * Calculates the total cost of issuing the bond
   */
  public getTotalIssuanceCost(): number {
    const parAmount = this.getTotalPrincipal();
    const debtService = this.getTotalDebtService();
    const capitalized = this.getTotalCapitalizedInterest();

    return SourcesAndUsesUtils.calculateTotalIssuanceCost(
      this.sourcesAndUses,
      parAmount,
      debtService,
      capitalized
    );
  }

  /**
   * Updates the yield curve
   */
  public async updateYieldCurve(curveType: "ust" | "eod_aaa" | "real_time_aaa" | number[]) {

    const fetch = (
      curveType === "ust" ? this.getAlignedUSTreasury :
        curveType === "eod_aaa" ? this.getAlignedAAA :
          curveType === "real_time_aaa" ? this.getAlignedRealTimeAAA :
            (_a: any, _b: any) => [curveType]
    );

    const [curve] = await fetch(this.marketConditionsDate, this.bonds.map(x => x.maturity));

    for (let index = 0; index < this.bonds.length; index++) {
      const row = this.production.find(x => x.bond === this.bonds[index]);
      if (!row) continue;
      const curveDiff = curve[index] - row.curve;
      row.curve += curveDiff;
    }

    this.updateProduction();
  }

  /**
   * Sets custom yields for the configured bonds.
   */
  public setCustomYields(yields: (number | undefined)[]) {
    for (let i = 0; i < yields.length && i < this.bonds.length; i++) {
      const newYield = yields[i];
      const bond = this.bonds[i];
      const row = this.production.find(x => x.bond === bond);
      if (!row || !newYield) continue;

      const diff = newYield - (row.curve + row.spread);
      row.spread += diff;
      row.locked = true;
    }

    this.updateProduction();
  }

  public async setSplineParPricingButton() {
    this.bonds.forEach(bond => {
      const prodRow = this.production.find(x => x.bond === bond);
      if (!prodRow || bond.lockedCoupon) return;
      bond.coupon = prodRow.curve + prodRow.spread;
    });

    this.updateDebtService();
    await this.updateProduction();
  }

  public async setSplineCouponPrediction() {
    const output = await this.fetchCouponsAndSpreads(this, this.bonds, true);
    if (!output.valid) return;

    output.body.forEach((value, index) => {
      const bond = this.bonds[index];
      if (bond.lockedCoupon) return;
      bond.coupon = value.coupon;
      bond.lockedCoupon = true;
    });

    this.updateDebtService();
    await this.updateProduction(this.bonds);
  }

  public async setCustomYield(value: number) {
    for (const row of this.production) {
      const diff = value - row.spread - row.curve;
      row.locked = true;
      row.spread += diff;
    }

    this.updateDebtService();
    await this.updateProduction();
  }

  public async setCushion(cushion: number) {
    const cushionDiff = cushion - this.cushion;

    for (const row of this.production) {
      row.spread += cushionDiff;
    }

    this.cushion = cushion;
    await this.updateProduction();
  }

  public async save(): Promise<Result<undefined>> {
    const result = await this.saveDeal(this);
    if (result.valid) this.isSaved = true;
    return result;
  }

  /**
   * Returns the variance and the expenses adjusted variance.
   */
  public calculateVariance(): [number | undefined, number | undefined] {
    if (!this.tic) return [undefined, undefined];

    const findInterestAtRate = (rate: number) => {
      const coupons = this.bonds.map(x => x.coupon);
      this.bonds.forEach(bond => { bond.coupon = rate; });
      this.updateDebtService();
      const totalInterest = this.getTotalInterest();
      for (let i = 0; i < this.bonds.length; i++) {
        this.bonds[i].coupon = coupons[i];
      }
      this.updateDebtService();
      return totalInterest;
    }

    const bidRateInterest = findInterestAtRate(this.bidRate);
    const ticRateInterest = findInterestAtRate(this.tic);
    const variance = bidRateInterest - ticRateInterest;

    const expensesTicRateInterest = this.adjustedTic ?
      findInterestAtRate(this.adjustedTic) :
      undefined;

    const expensesVariance = expensesTicRateInterest ?
      bidRateInterest - expensesTicRateInterest :
      undefined;

    return [variance, expensesVariance];
  }
}
